// The one HTTP wrapper (spec §13): same-origin credentials, the CSRF header on every mutation,
// the `{ data } | { error }` envelope parsed into a typed result, and a 401 mid-session → one
// reload, after which the boot-time /api/me answers 401 and MeProvider renders the sign-in page in
// place at the same URL (never a bounce to /auth/login — that is the button's job).
//
// Why a result object rather than a thrown error: every data-bearing component has to render a
// permission-denied, not-configured or unavailable state distinct from a generic failure
// (03-components §1, app-baseline 05-client §5.4), and a discriminated union keeps that
// decision explicit at the call site instead of buried in a catch.
import type { ApiErrorBody, ApiSuccess } from '../shared/api-types.js';
import { API_ERROR_CODES, type ApiErrorCode } from '../shared/enums.js';
import { safeReturnPath } from '../shared/text.js';

/** Required on every mutating /api and /auth request (server: auth/authenticate.ts). */
export const CSRF_HEADER = 'x-calis-request';
/** Echoed by the server on every response; the correlation id a user can quote. */
export const TRACE_HEADER = 'x-c4s-trace-id';

export type ApiFailureCode = ApiErrorCode | 'network_error' | 'unexpected_response' | 'timeout';

export interface ApiFailure {
  ok: false;
  /** HTTP status; 0 when the request never reached the server. */
  status: number;
  code: ApiFailureCode;
  /** Plain language from the server, or our own for transport failures. Safe to show. */
  message: string;
  correlationId: string | null;
  /** Field names that failed validation, when the server says which. */
  fields?: string[];
  /** True when the caller's AbortSignal cancelled the request — not a failure to report. */
  aborted?: boolean;
}

export interface ApiOk<T> {
  ok: true;
  status: number;
  data: T;
}

export type ApiResult<T> = ApiOk<T> | ApiFailure;

export interface RequestOptions {
  signal?: AbortSignal;
  /** Skip the 401 → reload; the public pages and the boot-time /api/me (which renders sign-in itself) ask for this. */
  noRedirect?: boolean;
  /** The ceiling on this request's wait, in milliseconds (default `REQUEST_TIMEOUT_MS`). */
  timeoutMs?: number;
}

/**
 * The longest any request waits for its answer, body included, before it is given up as failed (application baseline
 * 1.1.1, 05-client §5.5: a busy control's wait "MUST NOT be indefinite (§5.4)"; Phase 1c finding S6). Why here, in the
 * one wrapper every call goes through: nothing else Calis owns bounds a stalled connection — the server sets no request
 * or handler timeout, and the production proxy is not in the repository — so a button held busy, or a list loading,
 * could otherwise wait for good. Why a minute: above the longest bound a request's own work can reach (a Slack call's
 * 15 s per method plus up to 30 s waiting out a rate limit, `slack/client.ts`; Fellow's 30 s, `fellow/client.ts`), so it
 * ends only a wait that is no longer going to answer.
 */
export const REQUEST_TIMEOUT_MS = 60_000;

/** Routes that render without a session; a 401 on them must never trigger the reload. */
export const PUBLIC_PATHS = ['/signed-out', '/dev-sign-in'] as const;

export function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));
}

/** The server decides between Google and dev sign-in; the client only says where to come back to. */
export function signInUrl(returnTo?: string | null): string {
  const target = safeReturnPath(returnTo) ?? '/';
  return `/auth/login?returnTo=${encodeURIComponent(target)}`;
}

let redirecting = false;

/**
 * A 401 mid-session (expired, suspended, revoked): a full reload, not a router push, so the app
 * state goes with the session. The app boots again at the same URL, /api/me answers 401, and
 * MeProvider renders the sign-in page in place — whose returnTo is that URL, so nothing is lost
 * and nobody is bounced to Google unasked. The latch keeps the burst of 401s from a page's
 * parallel requests to one reload.
 */
export function redirectToSignIn(): void {
  if (typeof window === 'undefined' || redirecting) return;
  redirecting = true;
  window.location.reload();
}

const TRANSPORT_MESSAGES: Record<'network_error' | 'unexpected_response' | 'timeout', string> = {
  network_error: "We couldn't reach Calis. Check your connection and try again.",
  unexpected_response: 'Calis answered in a way we did not expect. Try again; if it keeps happening, quote the reference below.',
  timeout: 'Calis did not answer within a minute, so we stopped waiting. Check your connection and try again.',
};

/** A fallback per code, for the rare error body that arrives without a message. */
const CODE_MESSAGES: Partial<Record<ApiErrorCode, string>> = {
  unauthenticated: 'Your session has ended. Sign in again to continue.',
  forbidden: 'You do not have access to this. Ask an administrator if you need it.',
  csrf_required: 'The request was missing its safety header. Reload the page and try again.',
  feature_not_configured: 'This feature is not switched on in this installation.',
  not_found: "We couldn't find that. It may have been removed.",
  conflict: 'That change conflicts with the current state. Reload and try again.',
  self_lockout: 'You cannot apply that to your own account.',
  superuser_env_managed: 'This account is a superuser set by the deployment; it is removed there, not here.',
  group_required: 'Choose at least one group.',
  sign_in_pending: 'Your access is waiting for an administrator to approve it.',
  rate_limited: 'Too many requests in a short time. Wait a moment and try again.',
  not_ready: 'Calis is still starting. Try again in a moment.',
  upstream_unavailable: 'A service Calis depends on did not answer. Try again in a moment.',
  internal_error: 'Something failed on our side. Try again; if it keeps happening, quote the reference below.',
  invalid_request: 'Some of the details were not accepted. Check them and try again.',
};

function isErrorCode(value: unknown): value is ApiErrorCode {
  return typeof value === 'string' && (API_ERROR_CODES as readonly string[]).includes(value);
}

/**
 * Turns a raw response into a result. Exported so the envelope rules are unit-tested without a
 * browser: a 2xx without `data`, or a 4xx without `error`, is an unexpected response, not a crash.
 */
export function parseResponse<T>(status: number, traceId: string | null, bodyText: string): ApiResult<T> {
  let parsed: unknown = null;
  if (bodyText.trim() !== '') {
    try {
      parsed = JSON.parse(bodyText);
    } catch {
      parsed = undefined;
    }
  }
  const record = parsed !== null && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;

  if (status >= 200 && status < 300) {
    if (record && 'data' in record) return { ok: true, status, data: (record as unknown as ApiSuccess<T>).data };
    if (status === 204) return { ok: true, status, data: undefined as T };
    return { ok: false, status, code: 'unexpected_response', message: TRANSPORT_MESSAGES.unexpected_response, correlationId: traceId };
  }

  const error = record && typeof record.error === 'object' && record.error !== null ? (record as unknown as ApiErrorBody).error : null;
  if (error && isErrorCode(error.code)) {
    const message = typeof error.message === 'string' && error.message.trim() !== ''
      ? error.message
      : CODE_MESSAGES[error.code] ?? TRANSPORT_MESSAGES.unexpected_response;
    const failure: ApiFailure = {
      ok: false,
      status,
      code: error.code,
      message,
      correlationId: typeof error.correlationId === 'string' && error.correlationId !== '' ? error.correlationId : traceId,
    };
    if (Array.isArray(error.fields)) failure.fields = error.fields.filter((field): field is string => typeof field === 'string');
    return failure;
  }
  // Why: a proxy or the rate limiter may answer with a bare status; map the well-known ones so
  // the UI still says something truthful rather than "unexpected".
  const fallbackCode: ApiFailureCode = status === 401 ? 'unauthenticated'
    : status === 403 ? 'forbidden'
      : status === 404 ? 'not_found'
        : status === 429 ? 'rate_limited'
          : status === 503 ? 'not_ready'
            : status >= 500 ? 'internal_error'
              : 'unexpected_response';
  const message = fallbackCode === 'unexpected_response' ? TRANSPORT_MESSAGES.unexpected_response : CODE_MESSAGES[fallbackCode] ?? TRANSPORT_MESSAGES.unexpected_response;
  return { ok: false, status, code: fallbackCode, message, correlationId: traceId };
}

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export async function request<T>(method: HttpMethod, path: string, body?: unknown, options: RequestOptions = {}): Promise<ApiResult<T>> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (method !== 'GET') headers[CSRF_HEADER] = '1';
  if (body !== undefined) headers['content-type'] = 'application/json';

  // Why a controller of our own rather than the caller's signal alone: it ends the wait at REQUEST_TIMEOUT_MS as well as
  // when the caller cancels — and only the caller's cancel is `aborted`, a result to ignore; a timeout is a failure to
  // show. Combined by hand rather than `AbortSignal.any`, which older Safari lacks.
  const controller = new AbortController();
  let timedOut = false;
  const cancel = (): void => controller.abort();
  if (options.signal?.aborted) controller.abort();
  else options.signal?.addEventListener('abort', cancel, { once: true });
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, options.timeoutMs ?? REQUEST_TIMEOUT_MS);
  const timeoutFailure = (status: number, correlationId: string | null): ApiFailure => ({ ok: false, status, code: 'timeout', message: TRANSPORT_MESSAGES.timeout, correlationId, aborted: false });

  try {
    let response: Response;
    try {
      response = await fetch(path, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        credentials: 'same-origin',
        signal: controller.signal,
      });
    } catch (error) {
      if (timedOut) return timeoutFailure(0, null);
      const aborted = options.signal?.aborted === true || (error instanceof DOMException && error.name === 'AbortError');
      return { ok: false, status: 0, code: 'network_error', message: aborted ? 'The request was cancelled.' : TRANSPORT_MESSAGES.network_error, correlationId: null, aborted };
    }

    const traceId = response.headers.get(TRACE_HEADER);
    let text = '';
    try {
      text = await response.text();
    } catch {
      // Why the timeout is told apart here too: a body that stalls mid-way is the same wait as an answer that never came.
      if (timedOut) return timeoutFailure(response.status, traceId);
      return { ok: false, status: response.status, code: 'unexpected_response', message: TRANSPORT_MESSAGES.unexpected_response, correlationId: traceId };
    }
    const result = parseResponse<T>(response.status, traceId, text);

    if (!result.ok && result.status === 401 && !options.noRedirect && typeof window !== 'undefined' && !isPublicPath(window.location.pathname)) {
      redirectToSignIn();
    }
    return result;
  } finally {
    clearTimeout(timer);
    options.signal?.removeEventListener('abort', cancel);
  }
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>('GET', path, undefined, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>('POST', path, body, options),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>('PUT', path, body, options),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>('PATCH', path, body, options),
  delete: <T>(path: string, options?: RequestOptions) => request<T>('DELETE', path, undefined, options),
};

/** Builds a query string, dropping empty values so the URL stays readable. */
export function queryString(params: Record<string, string | number | boolean | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

export type FailureKind = 'denied' | 'not-configured' | 'unavailable' | 'not-found' | 'error';

/**
 * Which of the shared states a failure maps to (03-components §1; app-baseline 05-client §5.4
 * distinguishes not-configured from failing). 401 never reaches here on an app page — the
 * wrapper has reloaded — but is classed as an error if it does.
 */
export function failureKind(failure: Pick<ApiFailure, 'status' | 'code'>): FailureKind {
  if (failure.code === 'feature_not_configured') return 'not-configured';
  if (failure.code === 'forbidden' || failure.status === 403) return 'denied';
  if (failure.code === 'not_found') return 'not-found';
  if (failure.code === 'upstream_unavailable' || failure.code === 'not_ready' || failure.code === 'network_error' || failure.code === 'timeout' || failure.code === 'rate_limited') return 'unavailable';
  return 'error';
}
